import React, { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';
const STORAGE_KEY = 'vst_offer_expires_at';
const OFFER_DURATION_MS = 48 * 60 * 60 * 1000; // 48 hours
function getExpiryTimestamp(): number {
  if (typeof window === 'undefined') return Date.now() + OFFER_DURATION_MS;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const ts = parseInt(stored, 10);
      // If still in the future, use it. Otherwise reset.
      if (!isNaN(ts) && ts > Date.now()) return ts;
    }
    const newTs = Date.now() + OFFER_DURATION_MS;
    window.localStorage.setItem(STORAGE_KEY, String(newTs));
    return newTs;
  } catch {
    return Date.now() + OFFER_DURATION_MS;
  }
}
function format(n: number) {
  return n.toString().padStart(2, '0');
}
interface CountdownTimerProps {
  variant?: 'inline' | 'banner';
}
export function CountdownTimer({
  variant = 'inline'
}: CountdownTimerProps) {
  const [expiry, setExpiry] = useState<number | null>(null);
  const [remaining, setRemaining] = useState({
    h: 0,
    m: 0,
    s: 0
  });
  useEffect(() => {
    const ts = getExpiryTimestamp();
    setExpiry(ts);
  }, []);
  useEffect(() => {
    if (!expiry) return;
    const tick = () => {
      const diff = Math.max(0, expiry - Date.now());
      const h = Math.floor(diff / (1000 * 60 * 60));
      const m = Math.floor(diff % (1000 * 60 * 60) / (1000 * 60));
      const s = Math.floor(diff % (1000 * 60) / 1000);
      setRemaining({
        h,
        m,
        s
      });
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [expiry]);
  if (variant === 'inline') {
    // Compact inline version (for inside UrgencyBar)
    return <span className="inline-flex items-center gap-1.5 font-mono font-semibold tabular-nums" data-id="element-87">
        <Clock size={14} data-id="element-88" />
        {format(remaining.h)}:{format(remaining.m)}:{format(remaining.s)}
      </span>;
  }
  // Banner version (larger, used above pricing)
  return <div className="flex flex-col items-center gap-3 bg-sage-800 text-cream rounded-2xl px-6 py-6 shadow-lg max-w-xl mx-auto" data-id="element-89">
      <div className="flex items-center gap-2 text-yellow-300" data-id="element-90">
        <Clock size={18} data-id="element-91" />
        <p className="text-xs md:text-sm font-semibold tracking-widest uppercase" data-id="element-92">
          La oferta termina en
        </p>
      </div>
      <div className="flex items-center gap-3 md:gap-4" data-id="element-93">
        {[{
        label: 'Horas',
        value: remaining.h
      }, {
        label: 'Min',
        value: remaining.m
      }, {
        label: 'Seg',
        value: remaining.s
      }].map(unit => <div key={unit.label} className="text-center" data-id="element-94">
            <div className="bg-sage-700 rounded-xl px-4 md:px-5 py-2 md:py-3 min-w-[64px] md:min-w-[80px]" data-id="element-95">
              <span className="text-3xl md:text-4xl font-bold font-mono tabular-nums" data-id="element-96">
                {format(unit.value)}
              </span>
            </div>
            <span className="text-[10px] md:text-xs text-sage-100/80 mt-1.5 block uppercase tracking-wider" data-id="element-97">
              {unit.label}
            </span>
          </div>)}
      </div>
    </div>;
}